import { keepsWeekView } from '@/lib/week/keepsWeekView'
import { currentWeekCleanup, weekRangeFromParams, weekStartParam } from '@/lib/week/weekStartParam'
import { useWeekRollover } from '@/hooks/useWeekRollover'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useState, useMemo, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { useCadenceConfig, readCadenceConfig, weekStartAnchor } from '@/lib/cadence/config'
import { HomeChromeControls } from './HomeChromeControls'
import type { HomeViewType } from '@/types/homeView'
import type { Task } from '@/types/task'
import type { DensitySources } from '@/lib/planning/dayDensity'
import type { Project } from '@/types/project'
import type { CalendarEvent } from '@/hooks/useGoogleCalendar'
import type { Routine, ActionableInstance } from '@/types/actionable'
import { useScheduleActionsContext } from '@/contexts/ScheduleActionsContext'
import { filterEventsForLayers, filterRoutinesForLayers, filterTasksForLayers, filterByLayers } from '@/lib/today/domainFilter'
import { dedupeCalendarEvents } from '@/lib/calendar/dedupeEvents'
import { domainById } from '@/lib/domains'
import { useHomeView } from '@/hooks/useHomeView'
import { useMobile } from '@/hooks/useMobile'
import { useUndo } from '@/hooks/useUndo'
import { useDomain } from '@/hooks/useDomain'
import { useAssigneeFilter } from '@/hooks/useAssigneeFilter'
import { makeAssigneeFilter } from '@/lib/today/assigneeFilter'
import { WeekView } from './WeekView'
import { WeekViewV2 } from './week/WeekViewV2'
import { MonthView } from './MonthView'

const WEEK_V2_FLAG = 'symphony-week-v2'
function isWeekV2Enabled(): boolean {
  if (typeof window === 'undefined') return false
  // Default ON. Users can opt out by running in the browser console:
  //   localStorage.setItem('symphony-week-v2', 'off'); location.reload()
  // Known Phase 4b gaps still to address: undo/toast wiring, prev/next-week
  // nav arrows, mobile events/routines rendering, DragOverlay block ghost.
  return localStorage.getItem(WEEK_V2_FLAG) !== 'off'
}
import { mondayOfWeek } from '@/lib/workweekHelpers'
import { findTaskById } from '@/lib/findTaskById'
import { sundayOfWeek } from '@/lib/weekHelpers'
import { CascadingRiverView } from './CascadingRiverView'
import { TodayView } from '@/components/schedule/TodayView'
import { PAGE_GUTTER_X } from '@/components/layout/pageLayout'
import { UndoToast } from '@/components/undo/UndoToast'
import { HomeHeader } from '@/components/home/HomeHeader'
import { PlanMastheadSlotsContext } from '@/components/plan/v2/planMastheadSlots'
import { CalendarReconnectBanner } from '@/components/home/CalendarReconnectBanner'
import { PLANNING_PAGE_CLASS } from '@/components/layout/pageLayout'

interface HomeViewProps {
  tasks: Task[]
  /** How each source stands for the range in view — handed to the week's day
   *  tiles so they never draw a day as quiet before they have read it. */
  densitySources?: DensitySources
  /** Whose day this is — focus (task_focus) is per person. */
  userId?: string | null
  events: CalendarEvent[]
  routines: Routine[]
  allActiveRoutines: Routine[]
  projects: Project[]
  dateInstances: ActionableInstance[]
  selectedItemId: string | null
  onSelectItem: (id: string | null) => void
  loading?: boolean
  /** The task read failed and nothing arrived — Today says so instead of
   *  drawing an empty day. */
  tasksLoadFailed?: boolean
  /** The tasks hook's refetch, for Try again. */
  onRetryTasks?: () => void
  viewedDate: Date
  onDateChange: (date: Date) => void
  bothPanelsOpen?: boolean
  /** Opens the plan-from-paper flow (photo of a written plan → placed tasks). */
  /** Pin this mount to one sub-view, ignoring useHomeView. The Week bench
   *  (`/week`) mounts HomeView with fixedView="week" — its own route, not a
   *  switcher state (the D/W/M switcher died with the analog-planning pivot). */
  fixedView?: HomeViewType
  /**
   * Register an undo with the HOST's stack instead of this component's own.
   *
   * Both this component and `HomeViewContainer` own a `useUndo()` and both
   * used to render an `UndoToast`, so a single routine completion — which the
   * container's ScheduleActions already registers — pushed to two independent
   * stacks and raised TWO Undo notifications (S3-12, seen live 2026-09-24).
   * Given this, the second stack goes unused and no second toast is drawn;
   * a double push then lands in one stack, where the later one replaces the
   * earlier, which is one notification.
   */
  registerUndo?: (message: string, undo: () => void) => void
  /** Today's planning reminder, drawn below its schedule (2026-09-22). */
  todayAfterSchedule?: ReactNode
}

export function HomeView({
  tasks,
  userId,
  events,
  densitySources,
  routines,
  allActiveRoutines,
  projects,
  dateInstances,
  selectedItemId,
  onSelectItem,
  loading,
  tasksLoadFailed,
  onRetryTasks,
  viewedDate,
  onDateChange,
  bothPanelsOpen,
  fixedView,
  registerUndo,
  todayAfterSchedule,
}: HomeViewProps) {
  const ctx = useScheduleActionsContext()
  const { currentView: hookView, setCurrentView } = useHomeView()
  const currentView = fixedView ?? hookView
  const isMobile = useMobile()
  const { currentAction, pushAction: ownPushAction, executeUndo, dismiss } = useUndo()
  const pushAction = registerUndo ?? ownPushAction
  const { layers, soleDomain } = useDomain()

  // Filter tasks, routines, projects, and events by the checked layer set.
  // Task scoping lives in filterTasksForLayers (shared with the Time-block
  // grid, which is launched from this page and must show the same day): an
  // item shows iff the layer its context maps to is checked — untagged items
  // are the Unsorted layer, not "everywhere". Life area only — never who
  // owns or is assigned the item; that is the assignee filter's job.
  const filteredTasks = useMemo(
    () => filterTasksForLayers(tasks, layers),
    [tasks, layers])

  // Neither TodayView nor CascadingRiverView consumes this for visibility any
  // more — both apply layer scoping themselves via resolveRoutine (rung 4)
  // against the raw `routines` prop. This memo survives only to feed
  // `hasUnassignedTasks` below, which wants the checked layers' routines.
  const filteredRoutines = useMemo(
    () => filterRoutinesForLayers(routines, layers),
    [routines, layers])

  const filteredProjects = useMemo(() => filterByLayers(projects, layers), [projects, layers])

  // Calendar events are layer-filtered like tasks. An event's context is
  // resolved (manual override → calendar→domain mapping → Unsorted). The
  // checked layers show their own events; an unmapped calendar sits in
  // Unsorted until mapped. This stops e.g. work-calendar events leaking into
  // the Family/Personal views.
  // dedupe FIRST, then filter by layer: the same meeting synced to two
  // calendars arrives twice, and every surface fed by `filteredEvents`
  // (Today, This Week, Month, the river) would otherwise have to remember to
  // collapse it. computeTodayData did remember, which is why the duplicates
  // stayed invisible until /week rendered the same data without it and showed
  // every school day and every dinner twice in overlapping lanes.
  const filteredEvents = useMemo(
    () => filterEventsForLayers(dedupeCalendarEvents(events), layers, {
      eventContextOverrides: ctx.eventContextOverrides,
      getDomainForCalendar: ctx.getDomainForCalendar,
      eventNotesMap: ctx.eventNotesMap,
    }),
    [events, layers, ctx.eventContextOverrides, ctx.getDomainForCalendar, ctx.eventNotesMap])

  // Assignee filter state — persisted, and defaulting to EVERYONE.
  //
  // This used to seed `[currentUserMemberId]` on a first-ever load. Two people
  // in one household therefore opened the same day and saw two different
  // agendas: a family routine assigned to Ella was simply absent from Scott's
  // Today, from a row he could already fetch. Worse, `makeAssigneeFilter(['me'])`
  // returns false for an UNASSIGNED item (only the pseudo-id 'unassigned'
  // matches those), so the default also swallowed every unclaimed household
  // task and routine — the ones most likely to be dropped.
  //
  // The household's day is the default view; narrowing to one person is a lens
  // you reach for. The key is rotated to -v2 so the browsers that already
  // stored a self-filter under the old key adopt the new default once; an
  // explicit choice made after that is stored and wins.
  // The lens is shared with the Today pin (useAssigneeFilter), so both narrow
  // rows and counts identically.
  const [selectedAssignees, setSelectedAssignees] = useAssigneeFilter()

  const showRiverView = useMemo(() => {
    const realMemberCount = selectedAssignees.filter(id => id !== 'unassigned').length
    return realMemberCount >= 2
  }, [selectedAssignees])

  // Keep the assignee selection across view switches (don't reset to Everyone).
  const handleViewChange = useCallback((view: typeof currentView) => {
    setCurrentView(view)
  }, [setCurrentView])

  // The week draws events through the same people filter as Today (an event's
  // people live on its note). Tasks are narrowed inside the week, which also
  // needs the whole list for lookups.
  const weekEvents = useMemo(() => {
    if (selectedAssignees.length === 0) return filteredEvents
    const match = makeAssigneeFilter(selectedAssignees)
    return filteredEvents.filter((event) => {
      const note = ctx.eventNotesMap?.get(event.google_event_id || event.id)
      return match(note?.assignedTo, note?.assignedToAll)
    })
  }, [filteredEvents, selectedAssignees, ctx.eventNotesMap])

  const selectedAssigneeForSchedule = useMemo(() => {
    if (selectedAssignees.length === 0) return null
    if (selectedAssignees.length === 1) return selectedAssignees[0]
    return null
  }, [selectedAssignees])

  // Bulk update handler for inbox triage
  const handleUpdateTasksBulk = useCallback(async (taskIds: string[], updates: Partial<Task>) => {
    if (!ctx.onUpdateTask) return
    for (const taskId of taskIds) {
      ctx.onUpdateTask(taskId, updates)
    }
  }, [ctx.onUpdateTask])

  // Check for unassigned tasks/events/routines
  const hasUnassignedTasks = useMemo(() => {
    for (const task of filteredTasks) {
      if (!task.completed && !task.assignedTo && (!task.assignedToAll || task.assignedToAll.length === 0)) {
        return true
      }
    }
    for (const event of filteredEvents) {
      const eventId = event.google_event_id || event.id
      const eventNote = ctx.eventNotesMap?.get(eventId)
      if (!eventNote?.assignedTo && (!eventNote?.assignedToAll || eventNote.assignedToAll.length === 0)) {
        return true
      }
    }
    for (const routine of filteredRoutines) {
      if (!routine.assigned_to && (!routine.assigned_to_all || routine.assigned_to_all.length === 0)) {
        return true
      }
    }
    return false
  }, [filteredTasks, filteredEvents, filteredRoutines, ctx.eventNotesMap])

  // Week view state. Anchored to the user's "Week starts on" (Settings →
  // Planning Rhythm), which every other week reader already honours through
  // weekStartAnchor — the grid was the one place still hardcoded to Monday
  // (since 2026-02), so a Sunday setting silently did nothing here while the
  // task buckets it placed into moved.
  const { config: cadenceConfig } = useCadenceConfig()
  const weekStartsOn = cadenceConfig.weekStartsOn
  const [weekStart, setWeekStart] = useState(() => weekStartAnchor(new Date(), readCadenceConfig().weekStartsOn))
  // How many days /week draws from weekStart. 7 is the week; the masthead's
  // presets and custom start/end set fewer. A range is a VIEW, never a bucket
  // — it changes what is drawn and writes nothing (Scott, 2026-09-06: the
  // time-block overlay's range picker moved here and the overlay went).
  const location = useLocation()
  const [, setSearchParams] = useSearchParams()
  const rangePreset = new URLSearchParams(location.search).get('range')
  const startParam = new URLSearchParams(location.search).get('start')
  const [rangeDays, setRangeDays] = useState(7)
  // Desktop /week folds the week's control row into its masthead (layout
  // system, 2026-10-01): HomeHeader draws two hosts, WeekV2 portals its plan
  // status and controls into them. Phones keep the row.
  const [planSublineHost, setPlanSublineHost] = useState<HTMLElement | null>(null)
  const [planControlsHost, setPlanControlsHost] = useState<HTMLElement | null>(null)
  const planSlotsOn = currentView === 'week' && !isMobile
  const planSlots = useMemo(
    () => (planSlotsOn && planSublineHost && planControlsHost ? { subline: planSublineHost, controls: planControlsHost } : null),
    [planSlotsOn, planSublineHost, planControlsHost])
  // Week is a planning page: it fills the screen up to 1600px of content
  // (Scott, 2026-10-03).
  const weekColumn = (currentView === 'week' || currentView === 'workweek') && isWeekV2Enabled()
    ? ` week-column w-full max-w-[1712px] mx-auto ${PLANNING_PAGE_CLASS}`
    : ''
  // Set by goToWeek and onRangeChange: the run they just wrote into the URL is
  // already on screen, so re-deriving it would only redo what the reader just did.
  const selfWeekNav = useRef(false)
  // A range picked from the masthead goes into the URL too, or a reload (or
  // Back from a task) would drop it for the full week. Replace, not push: the
  // custom start/end inputs fire on every edit, and each is not a place to go
  // Back to.
  const onRangeChange = useCallback((range: Date[]) => {
    setWeekStart(range[0])
    setRangeDays(range.length)
    onDateChange(range[0])
    const next = weekStartParam(fixedView, range.length, location.search, range[0], readCadenceConfig().weekStartsOn, new Date())
    if (!next) return
    selfWeekNav.current = true
    setSearchParams(next, { replace: true })
  }, [onDateChange, fixedView, location.search, setSearchParams])
  /**
   * Page the week — and put it in the URL (S2-25).
   *
   * `weekStart` lived only in this component's state, so the week you paged to
   * existed nowhere durable: reload, or open a task and come back, and /week
   * re-derived the week from `new Date()` and returned you to this one
   * ("Assign a week, reload Week → Week reloads to September"). `?start=` is
   * already READ on arrival, so writing it makes reload, Back and Forward all
   * land where you were — the same contract `/month` got in S2-16.
   *
   * A weekend or a custom run also writes its `range` and `days` — `start`
   * alone would reopen it as the full week around that day.
   */
  const goToWeek = useCallback((weekAnchor: Date, viewed: Date = weekAnchor) => {
    setWeekStart(weekAnchor)
    onDateChange(viewed)
    const next = weekStartParam(fixedView, rangeDays, location.search, weekAnchor, readCadenceConfig().weekStartsOn, new Date())
    if (!next) return
    selfWeekNav.current = true
    setSearchParams(next, { replace: false })
  }, [onDateChange, fixedView, rangeDays, location.search, setSearchParams])

  const onDateChangeRef = useRef(onDateChange)
  const viewedDateRef = useRef(viewedDate)
  useEffect(() => {
    onDateChangeRef.current = onDateChange
    viewedDateRef.current = viewedDate
  })
  // Arriving at /week — from the navigation's Week menu or anywhere else —
  // opens the seven-day week unless the link names a shorter run
  // (?range=weekend | three) or a specific week (?start=YYYY-MM-DD, from a
  // planning nudge naming a week that isn't the current one), or carries the
  // exact run paged to (?start=…&range=…&days=N). Keyed on the
  // navigation itself, so choosing "Weekend" twice, or "Open week page"
  // after a weekend, re-applies.
  const previousWeekLocation = useRef<{ pathname: string; search: string } | null>(null)
  useEffect(() => {
    const keepView = keepsWeekView(previousWeekLocation.current, location)
    previousWeekLocation.current = { pathname: location.pathname, search: location.search }
    if (selfWeekNav.current) { selfWeekNav.current = false; return }
    if (fixedView !== 'week' || keepView) return
    // A link that names the current week ("Open week 41 →") arrives as the
    // bare page, so the page keeps showing the current week after it ends.
    const clean = currentWeekCleanup(location.search, readCadenceConfig().weekStartsOn, new Date())
    if (clean) { setSearchParams(clean, { replace: true }); return }
    const range = weekRangeFromParams(new URLSearchParams(location.search), readCadenceConfig().weekStartsOn, new Date())
    setWeekStart(range[0])
    setRangeDays(range.length)
    // The event fetch follows viewedDate (its week and the next); a weekend
    // chosen on a Sunday sits in next week, so move it along when needed.
    if (sundayOfWeek(viewedDateRef.current).getTime() !== sundayOfWeek(range[0]).getTime()) {
      onDateChangeRef.current(range[0])
    }
  }, [fixedView, location.key, location.pathname, location.search, setSearchParams])

  // Left open on the current week, the page moves on when the week ends.
  useWeekRollover(weekStart, rangeDays === 7, fixedView === 'week', useCallback((next: Date) => {
    setWeekStart(next)
    onDateChangeRef.current(next)
  }, []))

  // Changing the setting re-anchors the week on screen. Without this the view
  // keeps whatever the initial state captured until a remount, so the setting
  // appears not to work at the exact moment you change it. Only on a real
  // CHANGE: on mount it would re-anchor a weekend back to the week's start.
  const prevWeekStartsOn = useRef(weekStartsOn)
  useEffect(() => {
    if (prevWeekStartsOn.current === weekStartsOn) return
    prevWeekStartsOn.current = weekStartsOn
    setWeekStart((prev) => weekStartAnchor(prev, weekStartsOn))
    setRangeDays(7)
  }, [weekStartsOn])

  const [monthStart, setMonthStart] = useState(() => {
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), 1)
  })

  const handleSelectDay = (date: Date) => {
    onDateChange(date)
    setCurrentView('today')
  }

  // Wrap callbacks with undo functionality
  const handleToggleTaskWithUndo = useCallback((taskId: string) => {
    // Read the prior state from the FULL task list, not `filteredTasks`:
    // carried-over / overdue items are excluded from the today list, so looking
    // them up there returned undefined and mislabeled the toast. And through
    // the shared nested lookup, not a flat `.find` — per-person items are
    // SUBTASKS, so a flat scan missed them exactly the same way, and `toggleTask`
    // (which this has to agree with) has always walked the nesting.
    const task = findTaskById(tasks, taskId)
    const wasCompleted = task?.completed ?? false
    const result = ctx.onToggleTask(taskId)
    // Undo sets the EXPLICIT prior state via updateTask — it must NOT call
    // onToggleTask again. `toggleTask` derives the next value from a snapshot of
    // `tasks` captured when this handler was built (still showing the task as
    // incomplete), so a second toggle would re-complete it instead of reverting
    // — the "undo does nothing" bug. Writing `completed` explicitly is immune to
    // that stale closure.
    // Only once it saved: a failed write has already rolled the row back and
    // said so, and "Task completed · Undo" beside that would be a false claim.
    void Promise.resolve(result).then((ok) => {
      if (ok === false) return
      pushAction(
        wasCompleted ? 'Task marked incomplete' : 'Task completed',
        () => ctx.onUpdateTask?.(taskId, { completed: wasCompleted })
      )
    })
  }, [tasks, ctx.onToggleTask, ctx.onUpdateTask, pushAction])

  const handleDeleteTaskWithUndo = useCallback((taskId: string) => {
    if (!ctx.onDeleteTask) return
    const task = filteredTasks.find(t => t.id === taskId)
    if (!task) return
    ctx.onDeleteTask(taskId)
    pushAction(`Deleted "${task.title}"`, () => {})
  }, [filteredTasks, ctx.onDeleteTask, pushAction])

  // Routines and events have NO wrapper here. `useScheduleActions` awaits the
  // instance write and registers the one named confirmation itself ("Completed
  // \u201cSchool run\u201d"); a wrapper around it announced a second, generic one
  // before the write had even been attempted, so one gesture produced two Undo
  // notifications and the first of them could be a lie (Codex review,
  // 2026-09-24). A task still needs its wrapper: `onToggleTask` registers
  // nothing, and its Undo has to write the explicit prior state.

  const renderContent = () => {
    if (currentView === 'month') {
      return (
        <MonthView
          tasks={filteredTasks}
          events={filteredEvents}
          routines={allActiveRoutines}
          dateInstances={dateInstances}
          monthStart={monthStart}
          onMonthChange={setMonthStart}
          onSelectDay={handleSelectDay}
          selectedAssignee={selectedAssigneeForSchedule}
          layers={layers}
          eventNotesMap={ctx.eventNotesMap}
        />
      )
    }

    if (currentView === 'workweek') {
      // Workweek anchors weekStart to Monday (vs. Sunday for 7-day week).
      // We compute the displayed start locally so it doesn't permanently
      // shift the underlying weekStart state — switching back to Week keeps
      // the prior Sunday anchor.
      const mondayStart = mondayOfWeek(weekStart)
      return (
        <>
          <WeekViewV2
            tasks={filteredTasks}
            events={weekEvents}
            // Unfiltered, for the tiles' universal counts only.
            densityTasks={tasks}
            densityEvents={events}
            sources={densitySources}
            routines={allActiveRoutines}
            dateInstances={dateInstances}
            weekStart={mondayStart}
            dayCount={5}
            onWeekChange={(d) => goToWeek(sundayOfWeek(d), d)}
            selectedAssignee={selectedAssigneeForSchedule}
            selectedAssignees={selectedAssignees}
            layers={layers}
            onSelectItem={onSelectItem}
            onUpdateTask={ctx.onUpdateTask ?? (() => {})}
            onUpdateRoutine={ctx.onUpdateRoutine ?? (() => {})}
            onUpdateEvent={ctx.onUpdateEvent}
            onPushRoutine={ctx.onPushRoutine}
            pushAction={pushAction}
            onReorderTasks={ctx.onReorderTasks}
          />
        </>
      )
    }

    if (currentView === 'week') {
      const useV2 = isWeekV2Enabled()
      if (!useV2) {
        return (
          <WeekView
            tasks={filteredTasks}
            events={filteredEvents}
            routines={allActiveRoutines}
            dateInstances={dateInstances}
            weekStart={weekStart}
            onWeekChange={(d) => goToWeek(d)}
            onSelectDay={handleSelectDay}
            selectedAssignee={selectedAssigneeForSchedule}
            layers={layers}
            eventNotesMap={ctx.eventNotesMap}
          />
        )
      }
      return (
        <>
          <WeekViewV2
            tasks={filteredTasks}
            events={weekEvents}
            // Unfiltered, for the tiles' universal counts only.
            densityTasks={tasks}
            densityEvents={events}
            sources={densitySources}
            routines={allActiveRoutines}
            dateInstances={dateInstances}
            weekStart={weekStart}
            dayCount={rangeDays}
            // A range start is wherever the range starts: stepping moves the
            // run by its own length, so a weekend stays a weekend and a
            // custom Thu–Wed week stays Thu–Wed.
            onWeekChange={(d) => goToWeek(d)}
            selectedAssignee={selectedAssigneeForSchedule}
            selectedAssignees={selectedAssignees}
            layers={layers}
            onSelectItem={onSelectItem}
            onUpdateTask={ctx.onUpdateTask ?? (() => {})}
            onUpdateRoutine={ctx.onUpdateRoutine ?? (() => {})}
            onUpdateEvent={ctx.onUpdateEvent}
            onPushRoutine={ctx.onPushRoutine}
            pushAction={pushAction}
            onReorderTasks={ctx.onReorderTasks}
          />
        </>
      )
    }

    if (showRiverView) {
      return (
        <CascadingRiverView
          tasks={filteredTasks}
          events={filteredEvents}
          // Domain-UNfiltered on purpose, same reasoning as TodayView below:
          // the river now applies layer scoping itself via resolveRoutine
          // (rung 4), reading `layers` from useDomain() itself. Passing the
          // shared `filteredRoutines` memo here would double-filter (harmless,
          // since both predicates agree) but re-couples this surface to a memo
          // it no longer needs — pass the raw list so River owns its own rung 4.
          routines={routines}
          dateInstances={dateInstances}
          selectedItemId={selectedItemId}
          onSelectItem={onSelectItem}
          onToggleTask={handleToggleTaskWithUndo}
          onToggleWaiting={ctx.onToggleWaiting}
          onUpdateTask={ctx.onUpdateTask}
          onPushTask={ctx.onPushTask}
          onDeleteTask={handleDeleteTaskWithUndo}
          viewedDate={viewedDate}
          onDateChange={onDateChange}
          contactsMap={ctx.contactsMap}
          projectsMap={ctx.projectsMap}
          eventNotesMap={ctx.eventNotesMap}
          layers={layers}
          familyMembers={ctx.familyMembers}
          selectedAssignees={selectedAssignees}
          onSelectAssignees={setSelectedAssignees}
          onAssignTask={ctx.onAssignTask}
          onAssignEvent={ctx.onAssignEvent}
          onAssignRoutine={ctx.onAssignRoutine}
          onCompleteRoutine={ctx.onCompleteRoutine}
          onSkipRoutine={ctx.onSkipRoutine}
          onPushRoutine={ctx.onPushRoutine}
          onCompleteEvent={ctx.onCompleteEvent}
          onSkipEvent={ctx.onSkipEvent}
          onPushEvent={ctx.onPushEvent}
        />
      )
    }

    // Today view uses TodayView — it reads most props from context
    return (
      <TodayView
        headerControls={<HomeChromeControls className="flex" />}
        afterSchedule={todayAfterSchedule}
        tasks={filteredTasks}
        // The day tiles count universally — every domain, everyone — so they
        // are given the unfiltered list rather than the drawn one.
        densityTasks={tasks}
        userId={userId}
        allRoutines={allActiveRoutines}
        events={filteredEvents}
        // Domain-UNfiltered on purpose: TodayView's own pipeline applies
        // layer scoping via resolveRoutine (rung 4), reading `layers` from
        // useDomain() itself. CascadingRiverView (above) now does the
        // same, so `filteredRoutines` below is no longer shared for routine
        // visibility by either surface — it survives only to compute
        // `hasUnassignedTasks`.
        routines={routines}
        dateInstances={dateInstances}
        selectedItemId={selectedItemId}
        onSelectItem={onSelectItem}
        onToggleTask={handleToggleTaskWithUndo}
        onCompleteRoutine={ctx.onCompleteRoutine}
        onCompleteEvent={ctx.onCompleteEvent}
        loading={loading}
        tasksLoadFailed={tasksLoadFailed}
        onRetryTasks={onRetryTasks}
        viewedDate={viewedDate}
        onDateChange={onDateChange}
        projects={filteredProjects}
        selectedAssignees={selectedAssignees}
        onSelectAssignees={setSelectedAssignees}
        assigneesWithTasks={ctx.familyMembers}
        hasUnassignedTasks={hasUnassignedTasks}
        panelOpen={selectedItemId !== null}
        bothPanelsOpen={bothPanelsOpen}
        onClosePanel={() => onSelectItem(null)}
        onUpdateTasksBulk={handleUpdateTasksBulk}
        currentHomeView={currentView}
        onHomeViewChange={handleViewChange}
      />
    )
  }

  // Subtle domain background tint — only when exactly one real domain is checked.
  const tint = soleDomain ? domainById(soleDomain).bgClass : ''

  return (
    <div className={`relative flex flex-col h-full transition-colors duration-500 ${tint}`}>
      {/* Today header is rendered INSIDE the scroll container so it shares
          the exact same max-w/px column as TodayView's content, keeping the
          date label and controls left/right-aligned with the task rows.
          Week/Month headers stay outside the scroll container (full-width). */}
      {/* Week keeps its masthead on a phone too: it is the only way to step
          to another week or pick a shorter run there. */}
      {(!isMobile || currentView === 'week') && currentView !== 'today' && (
        <div className={`${PAGE_GUTTER_X} pt-2${weekColumn}`}>
          <HomeHeader
            currentView={currentView}
            onViewChange={handleViewChange}
            viewedDate={viewedDate}
            onDateChange={onDateChange}
            weekStart={weekStart}
            // Week nav must carry `viewedDate` along with the grid: the
            // container fetches events for the week containing viewedDate,
            // so a week viewedDate isn't in would render without its events.
            onWeekChange={(d) => goToWeek(d)}
            rangeDays={rangeDays}
            // Only the navigation's bare "Custom range…" asks for the inputs. A
            // custom run already in the URL (with its start) is restored as
            // days on screen, not re-opened for editing on every reload.
            customRangeRequest={rangePreset === 'custom' && !startParam ? location.key : undefined}
            onRangeChange={onRangeChange}
            monthStart={monthStart}
            onMonthChange={setMonthStart}
            planSlots={planSlotsOn ? { subline: setPlanSublineHost, controls: setPlanControlsHost } : undefined}
          />
        </div>
      )}

      {/* On desktop the shell scrolls the page; an inner scroller here (one that
          never scrolls — it grows with its content) made every sticky column
          inside it, the week's list and the month's calendar, stick to IT and
          so never stick at all (2026-09-29). Phones keep it for their header. */}
      <div className="flex-1 overflow-y-auto md:overflow-y-visible">
        {/* Today draws its own masthead inside the day card, and HomeHeader
            returns null for it — the padded wrapper that used to mount it
            here was an empty band above the date (2026-09-22). */}
        {/* Surfaces an expired/revoked calendar connection so the empty event
            state isn't silent. Wrapper collapses (empty:hidden) when the banner
            renders null, so it adds no padding while connected. */}
        {/* Today draws it under its own masthead, so every destination shows
            the heading first and notices beneath it (one page shape). */}
        {currentView !== 'today' && (
          <div className={`${PAGE_GUTTER_X} pt-4 empty:hidden`}>
            <CalendarReconnectBanner />
          </div>
        )}
        {currentView === 'today' || showRiverView
          ? renderContent()
          : (
            // Week/month/workweek draw no column of their own — they used to
            // start flush against the content edge, 56px left of their own
            // masthead card. One gutter, one left edge (Scott, 2026-09-07).
            <div className={`${PAGE_GUTTER_X}${weekColumn}`}>
              <PlanMastheadSlotsContext.Provider value={planSlots}>{renderContent()}</PlanMastheadSlotsContext.Provider>
            </div>
          )}
      </div>

      {/* Only when nobody above is showing one. Two mounted at once is how a
          single completion produced two Undo notifications. */}
      {!registerUndo && (
        <UndoToast
          action={currentAction}
          onUndo={executeUndo}
          onDismiss={dismiss}
        />
      )}
    </div>
  )
}
